// src/pages/CitizenAspirantProfile.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  Megaphone, 
  MapPin,
  Calendar,
  Mail,
  Phone,
  Award,
  Target,
  TrendingUp,
  MessageSquare,
  Share2,
  Globe
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const CitizenAspirantProfile: React.FC = () => {
  const { aspirantId } = useParams<{ aspirantId: string }>();
  const navigate = useNavigate();
  const [aspirant, setAspirant] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [memorandums, setMemorandums] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'memorandums'>('campaigns');

  useEffect(() => {
    if (aspirantId) {
      fetchAspirantData();
    }
  }, [aspirantId]);

  const fetchAspirantData = async () => {
    try {
      setLoading(true);
      
      // Fetch aspirant details
      const aspirantResponse = await apiService.getAspirant(aspirantId!);
      if (aspirantResponse.data.success) {
        setAspirant(aspirantResponse.data.data);
        
        // Fetch their campaigns
        const campaignsResponse = await apiService.getAspirantCampaigns(aspirantId!);
        if (campaignsResponse.data.success) {
          setCampaigns(campaignsResponse.data.data || []);
        }
        
        // Fetch their memorandums
        const memorandumsResponse = await apiService.getMemorandumsByAspirant(parseInt(aspirantId!));
        if (memorandumsResponse.data.success) {
          setMemorandums(memorandumsResponse.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching aspirant data:', error);
      toast.error('Failed to load aspirant profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f4f7f6]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2045] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading aspirant profile...</p>
        </div>
      </div>
    );
  }

  if (!aspirant) {
    return (
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="text-red-600" size={32} />
        </div>
        <h3 className="text-lg font-medium text-[#0b2045] mb-2">Aspirant not found</h3>
        <button
          onClick={() => navigate('/citizen/community')}
          className="px-6 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] transition-colors"
        >
          Back to Community
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f7f6]">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/citizen/community')}
          className="flex items-center text-gray-600 hover:text-[#0b2045] mb-6 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Community
        </button>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#0b2045] to-[#1a3d6e] rounded-2xl p-8 text-white mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-4xl font-bold">
                {aspirant.name.charAt(0)}
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">{aspirant.name}</h1>
              <p className="text-[#a8d59b] text-lg">{aspirant.position?.displayName || aspirant.position}</p>
              {aspirant.party && (
                <div className="inline-block mt-2 px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {aspirant.party}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
                <Share2 size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Contact Information</h3>
              <div className="space-y-3">
                {aspirant.phoneNumber && (
                  <div className="flex items-center text-[#0b2045]">
                    <Phone size={18} className="mr-3 text-gray-400" />
                    <span>{aspirant.phoneNumber}</span>
                  </div>
                )}
                {aspirant.email && (
                  <div className="flex items-center text-[#0b2045]">
                    <Mail size={18} className="mr-3 text-gray-400" />
                    <a href={`mailto:${aspirant.email}`} className="text-[#3c8d2e] hover:underline transition-colors">
                      {aspirant.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center text-[#0b2045]">
                  <Calendar size={18} className="mr-3 text-gray-400" />
                  <span>Joined {new Date(aspirant.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Representation Area</h3>
              <div className="space-y-3">
                <div className="flex items-center text-[#0b2045]">
                  <MapPin size={18} className="mr-3 text-gray-400" />
                  <div>
                    <p className="font-medium">Ward</p>
                    <p className="text-sm text-gray-600">{aspirant.ward || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center text-[#0b2045]">
                  <MapPin size={18} className="mr-3 text-gray-400" />
                  <div>
                    <p className="font-medium">Constituency</p>
                    <p className="text-sm text-gray-600">{aspirant.constituency || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Activity Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
                      <Megaphone className="text-[#0b2045]" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Campaigns</p>
                      <p className="text-lg font-bold text-[#0b2045]">{campaigns.length}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-[#0b2045]/10 rounded-lg mr-3">
                      <FileText className="text-[#0b2045]" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Memorandums</p>
                      <p className="text-lg font-bold text-[#0b2045]">{memorandums.length}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-[#0b2045]/10 mb-6">
              <div className="border-b border-[#0b2045]/10">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className={`flex-1 px-4 py-4 text-center font-medium ${
                      activeTab === 'campaigns'
                        ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                        : 'text-gray-600 hover:text-[#0b2045] hover:bg-[#f4f7f6]'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Megaphone size={18} />
                      Campaigns ({campaigns.length})
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('memorandums')}
                    className={`flex-1 px-4 py-4 text-center font-medium ${
                      activeTab === 'memorandums'
                        ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]'
                        : 'text-gray-600 hover:text-[#0b2045] hover:bg-[#f4f7f6]'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <FileText size={18} />
                      Memorandums ({memorandums.length})
                    </div>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {activeTab === 'campaigns' ? (
                  <div className="space-y-6">
                    {campaigns.length === 0 ? (
                      <div className="text-center py-8">
                        <Megaphone className="mx-auto text-gray-400 mb-4" size={48} />
                        <p className="text-gray-600">No campaigns published yet</p>
                      </div>
                    ) : (
                      campaigns.map((campaign) => (
                        <div key={campaign.id} className="border border-[#0b2045]/10 rounded-lg p-6 hover:bg-[#f4f7f6] transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-[#0b2045]">{campaign.campaignName}</h4>
                            <span className="text-xs text-[#0b2045] bg-[#f4f7f6] px-2 py-1 rounded">
                              {campaign.type?.displayName || campaign.type}
                            </span>
                          </div>
                          <p className="text-[#0b2045] mb-4">{campaign.description}</p>
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>{new Date(campaign.createdAt).toLocaleDateString()}</span>
                            {campaign.smsRecipients > 0 && (
                              <span>{campaign.smsRecipients.toLocaleString()} recipients</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {memorandums.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                        <p className="text-gray-600">No memorandums issued yet</p>
                      </div>
                    ) : (
                      memorandums.map((memo) => (
                        <div key={memo.id} className="border border-[#0b2045]/10 rounded-lg p-6 hover:bg-[#f4f7f6] transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-[#0b2045]">{memo.subject}</h4>
                            <span className="text-xs text-[#0b2045] bg-[#f4f7f6] px-2 py-1 rounded">
                              {memo.type?.displayName || memo.type}
                            </span>
                          </div>
                          <p className="text-[#0b2045] mb-4 line-clamp-3">{memo.content}</p>
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>To: {memo.recipientDepartment}</span>
                            <span>{new Date(memo.generatedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#f4f7f6] to-[#e8f1ec] border border-[#3c8d2e]/30 rounded-xl p-6">
              <div className="flex items-center">
                <div className="p-2 bg-[#3c8d2e]/10 rounded-lg mr-4">
                  <MessageSquare className="text-[#3c8d2e]" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#0b2045]">Engage with this Aspirant</h3>
                  <p className="text-[#0b2045]/80 text-sm mt-1">
                    You can reach out to this aspirant through their contact information or attend their campaign events.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitizenAspirantProfile;